export type LongevityAreaId = 'BODY' | 'MIND' | 'FINANCE' | 'COMMUNITY';

/** A = mantenere, M = rafforzare, B = priorità, '-' = area non calcolabile. */
export type LongevityGrade = 'A' | 'M' | 'B' | '-';

export interface LongevityAreaScore {
  area: LongevityAreaId;
  valid: number;
  applicable: number;
  /** Scala 0-10, null se l'area non è calcolabile. */
  score: number | null;
  grade: LongevityGrade;
}

/** FlagDTO = {id, kind}: tipo di soglia superata (es. area da approfondire). */
export interface LongevityFlag {
  id: string;
  kind: string;
}

/**
 * FamilyTutor selezionato nella pagina Avvisi. Il filtro `operator` di `/manager/cases`
 * vuole l'uid dell'operatore, il nome serve a raggruppare le righe.
 */
export interface LongevityOperatorTarget {
  name: string;
  /** Null se l'uid non è noto: i casi di quel FamilyTutor non si possono cercare. */
  uid: string | null;
}

export type ManagerCaseStatus = 'OPEN' | 'TAKEN_IN_CHARGE' | 'CLOSED';

export type ActivationReasonType =
  | 'AREA_BELOW_THRESHOLD'
  | 'CRITICAL_ANSWER'
  | 'MIND_COMBINED';

export interface LongevityActivationReason {
  /** Un tipo nuovo del backend si mostra com'è: vedi ACTIVATION_REASON_LABELS. */
  type: string;
  area: LongevityAreaId | null;
  /** Codici delle domande che hanno fatto scattare il caso. */
  questions: string[];
}

/** Stato del caso Manager di un test (AssessmentDetailDTO / AssessmentListItemDTO). */
export interface LongevityCaseInfo {
  caseId: number;
  status: ManagerCaseStatus;
  createdAt: Date;
  takenInChargeAt: Date | null;
  closedAt: Date | null;
  takenInChargeByYou: boolean;
}

/**
 * Una riga di `GET /manager/cases` (AssessmentListItemDTO): un test, con il suo caso Manager se
 * esiste. `id` è l'UUID del test (da usare per aprirlo e per il PATCH).
 */
export interface LongevityTestRow {
  id: string;
  requestId: number;
  operatorId: string | null;
  savedAt: Date;
  total: number | null;
  /** Le quattro lettere dei gradi: Corpo, Mente, Longevità finanziaria, Spazio collettivo. */
  code: string;
  caseId: number | null;
  status: ManagerCaseStatus | null;
  createdAt: Date | null;
  takenInChargeAt: Date | null;
  closedAt: Date | null;
  takenInChargeByYou: boolean;
}

/** Un test che ha aperto un caso Manager (coda della pagina Avvisi). */
export interface LongevityManagerCase extends LongevityTestRow {
  caseId: number;
  status: ManagerCaseStatus;
  /** Apertura del caso (caseCreatedAt). */
  createdAt: Date;
  /** Nome del FamilyTutor per cui è stata fatta la ricerca (non torna dal BE). */
  operator: string;
}

/** AssessmentDTO: il risultato di un test, con commento e piano scritti dallo staff. */
export interface LongevityAssessmentSummary {
  id: string;
  requestId: number;
  savedAt: Date;
  rulesVersion: string;
  answeredQuestions: number;
  total: number | null;
  /** Sempre nell'ordine di LONGEVITY_AREA_ORDER. */
  areas: LongevityAreaScore[];
  code: string;
  flags: LongevityFlag[];
  /** `result.comment` (string[]) e `result.plan` (string): testi dello staff, modificabili con il PATCH. */
  comment: string[];
  plan: string;
}

/** AssessmentDetailDTO: test per intero, per lo staff. */
export interface LongevityAssessmentDetail {
  assessment: LongevityAssessmentSummary;
  /** Le risposte come testo, per numero di domanda ("F3": "Spesso"); le domande in bianco mancano. */
  answers: Record<string, string>;
  operatorId: string | null;
  /** E-mail dell'account della persona: il backend la manda solo se il test ha un caso. */
  email: string | null;
  /** Null se il test non ha aperto nessun caso. */
  caseInfo: LongevityCaseInfo | null;
  reasons: LongevityActivationReason[];
}

/** Body del PATCH /assessments/{id}: almeno un campo, quelli omessi restano come sono. */
export interface LongevityAssessmentUpdate {
  /** Stringa vuota = cancella. Massimo 5000 caratteri. */
  comment?: string;
  /** Stringa vuota = cancella. Massimo 10000 caratteri. */
  plan?: string;
  status?: 'TAKEN_IN_CHARGE' | 'CLOSED';
  /** Uid del nuovo operatore della richiesta (vale per tutti i test della persona). */
  operatorId?: string;
}

export const LONGEVITY_COMMENT_MAX_LENGTH = 5000;
export const LONGEVITY_PLAN_MAX_LENGTH = 10000;

/** Ordine delle aree nel risultato e nel `code` (es. "AMAA"). */
export const LONGEVITY_AREA_ORDER: LongevityAreaId[] = [
  'BODY',
  'MIND',
  'FINANCE',
  'COMMUNITY',
];

export const LONGEVITY_AREA_LABELS: Record<LongevityAreaId, string> = {
  BODY: 'Corpo',
  MIND: 'Mente',
  FINANCE: 'Longevità finanziaria',
  COMMUNITY: 'Spazio collettivo',
};

export const LONGEVITY_GRADE_LABELS: Record<LongevityGrade, string> = {
  A: 'Mantenere',
  M: 'Rafforzare',
  B: 'Priorità',
  '-': 'Non calcolabile',
};

export const MANAGER_CASE_STATUS_LABELS: Record<ManagerCaseStatus, string> = {
  OPEN: 'Aperto',
  TAKEN_IN_CHARGE: 'Preso in carico',
  CLOSED: 'Chiuso',
};

export const ACTIVATION_REASON_LABELS: Record<ActivationReasonType, string> = {
  AREA_BELOW_THRESHOLD: 'Area sotto soglia',
  CRITICAL_ANSWER: 'Risposta critica',
  MIND_COMBINED: 'Combinazione su Mente',
};

export const LONGEVITY_TOTAL_QUESTIONS = 18;
